import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Check, X, UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const roleBadge = (role) =>
  role === "admin" ? (
    <Badge variant="secondary" className="gap-1"><ShieldCheck className="h-3 w-3" />Admin</Badge>
  ) : (
    <Badge variant="outline">Member</Badge>
  );

export default function AccessRequests() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });

  const act = useMutation({
    mutationFn: async ({ id, action }) => api.post(`/admin/users/${id}/${action}`),
    onSuccess: (_d, v) => {
      toast.success(v.action === "approve" ? "User approved" : "Request removed");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || "Action failed"),
  });

  const pending = users.filter((u) => u.status === "pending");
  const members = users.filter((u) => u.status === "approved");

  if (isLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-10" data-testid="access-requests-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Access management</h1>
        <p className="text-sm text-muted-foreground mt-1">Approve people requesting access and review current members.</p>
      </div>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="h-4 w-4 text-brand" />
          <h2 className="text-sm font-semibold">Pending requests</h2>
          <Badge variant="secondary" data-testid="pending-count">{pending.length}</Badge>
        </div>
        {pending.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg px-4 py-8 text-center">No pending requests right now.</p>
        ) : (
          <div className="space-y-2">
            {pending.map((u) => (
              <div key={u.id} className="flex items-center gap-3 border border-border rounded-lg px-4 py-3" data-testid={`pending-row-${u.email}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <Button size="sm" disabled={act.isPending} onClick={() => act.mutate({ id: u.id, action: "approve" })} data-testid={`approve-btn-${u.email}`}>
                  <Check className="h-4 w-4 mr-1" />Approve
                </Button>
                <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ id: u.id, action: "reject" })} data-testid={`reject-btn-${u.email}`}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-semibold">Members</h2>
          <Badge variant="secondary">{members.length}</Badge>
        </div>
        <div className="space-y-2">
          {members.map((u) => (
            <div key={u.id} className="flex items-center gap-3 border border-border rounded-lg px-4 py-3" data-testid={`member-row-${u.email}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{u.name}</div>
                <div className="text-xs text-muted-foreground truncate">{u.email}</div>
              </div>
              {roleBadge(u.role)}
              {u.role !== "admin" && (
                <Button size="sm" variant="ghost" disabled={act.isPending} onClick={() => act.mutate({ id: u.id, action: "reject" })} data-testid={`remove-btn-${u.email}`}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
